<template>
  <div
    :class="[
      $style.base,
      {
        [$style.isTouch]: touch,
        [$style.isMobile]: mobile
      }
    ]"
    v-test="'month-view'"
  >
    <div :class="$style.container" ref="container">
      <div :class="$style.monthSwitcher">
        <div
          :class="[
            $style.arrow,
            $style.arrowLeft,
            { [$style.isDisabled]: !canScrollLeft }
          ]"
          @click="slideLeft"
        >
          <IconArrow
            :isDisabled="loadingAvailability || !canScrollLeft"
            direction="left"
          />
        </div>
        <div :class="$style.monthName">
          {{ currentMonth }}
        </div>
        <div
          :class="[
            $style.arrow,
            $style.arrowRight,
            { [$style.isDisabled]: !canScrollRight }
          ]"
          @click="slideRight"
        >
          <IconArrow :isDisabled="!canScrollRight" direction="right" />
        </div>
      </div>
      <div :class="$style.list">
        <div
          v-for="day in daysOfTheWeek"
          :key="`day-${day}`"
          :class="$style.dayName"
        >
          {{ day }}
        </div>
      </div>
      <transition>
        <div :class="$style.list" ref="list">
          <div
            v-for="n in prevMonthDays"
            :key="`empty-${n}`"
            :class="$style.emptyDay"
          />
          <DatePickerItem
            v-for="(crtDate, index) in datesInMonth"
            :key="`date-${index}`"
            :data="getEmptyAvailability(crtDate)"
            :isLoading="
              !dayjs(crtDate).isBefore(dayjs()) &&
              loadingAvailability &&
              !hasAvailability(crtDate)
            "
            @select="() => selectDate(crtDate)"
            monthView
            v-test="'date-item'"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { device } from '@/user-context';

import IconArrow from '@/components/IconArrow.vue';
import DatePickerItem from './DatePickerItem.vue';

const emits = defineEmits(['loadAvailabilityData']);
const { d } = useI18n();

const { touch, mobile } = device;
const { settings } = useCompanyStore();

import { useAvailabilityStore } from '@/stores/availability';
import dayjs from 'dayjs';

const { availabilities, loadingAvailability } = storeToRefs(
  useAvailabilityStore()
);
const { getEmptyAvailability, hasAvailability } = useAvailabilityStore();

const canScrollLeft = computed(() =>
  dateMarker.value.subtract(1, 'month').endOf('month').isAfter(dayjs())
);
const canScrollRight = computed(() => !lastBookableDayIsRelevant.value);

const slideLeft = () =>
  (dateMarker.value = dateMarker.value.subtract(1, 'month').startOf('month'));
const slideRight = () => {
  if (!loadingAvailability.value) {
    dateMarker.value = dateMarker.value.add(1, 'month').startOf('month');
    loadRestOfMonth();
  }
};

const { date } = storeToRefs(useBookingStore());

const selectDate = (newDate: string) => {
  date.value = newDate;
};
const dateMarker = ref(date.value ? dayjs(date.value) : dayjs());

watch(date, () => {
  dateMarker.value = date.value ? dayjs(date.value) : dayjs();
});
const currentMonth = computed(() =>
  d(dateMarker.value.toISOString(), 'monthLong')
);

const lastDayRequested = ref(
  dayjs(availabilities.value[availabilities.value.length - 1].date)
);

watch(dateMarker, () => loadRestOfMonth());

const loadRestOfMonth = () => {
  const remainingDaysToLoad = dateMarker.value
    .endOf('month')
    .diff(lastDayRequested.value, 'day');

  if (remainingDaysToLoad > 0) {
    lastDayRequested.value = dateMarker.value.endOf('month');
    emits('loadAvailabilityData', remainingDaysToLoad);
  }
};

const exampleWeekDays = [
  '2021-02-01T00:00:00Z',
  '2021-02-02T00:00:00Z',
  '2021-02-03T00:00:00Z',
  '2021-02-04T00:00:00Z',
  '2021-02-05T00:00:00Z',
  '2021-02-06T00:00:00Z',
  '2021-02-07T00:00:00Z'
];
const daysOfTheWeek = exampleWeekDays.map((date) =>
  d(new Date(date), 'weekday')
);

const prevMonthDays = computed(() => {
  const dayOfWeek = dateMarker.value.startOf('month').day();
  return dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Shift day of week by 1, wrap around for Sunday
});

const lastBookableDayIsRelevant = computed(
  () =>
    settings.last_bookable_day &&
    dateMarker.value.endOf('month').isAfter(dayjs(settings.last_bookable_day))
);

const daysInMonth = computed(() =>
  dateMarker.value.endOf('month').diff(dateMarker.value.startOf('month'), 'day')
);
const datesInMonth = computed(() => {
  const startOfMonth = dateMarker.value.startOf('month');
  const dates: string[] = [];
  for (let i = 0; i <= daysInMonth.value; i++) {
    dates.push(startOfMonth.add(i, 'day').format('YYYY-MM-DD'));
  }
  return dates;
});

loadRestOfMonth();
</script>

<style lang="scss" module>
.base {
  position: relative;
  width: 100%;
  overflow: hidden;

  &.isTouch {
    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 0;
      width: $spacing;
      height: 100%;
      z-index: 1;
      pointer-events: none;
    }
  }
}
.monthSwitcher {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  .monthName {
    font-size: 22px;
    font-weight: bold;
    flex-grow: 1;
    text-align: center;
  }
}

.arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  position: inline-box;
  top: 0;
  height: 50px;
  width: $slider-btn-size;
  cursor: pointer;

  &.isDisabled {
    pointer-events: none;
  }
}

.container {
  padding-top: $spacing;

  .base:not(.isTouch) & {
    overflow: auto;

    //Hide scrollbars since overflow is auto
    -ms-overflow-style: none; // IE and Edge
    scrollbar-width: none; // Firefox

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .base.isMobile & {
    width: 100vw; // Fixes a mobile Safari bug with overflow-x: scroll, which seems to break when the container has no width, or a percentage based width
  }
}
.list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  align-items: center;
  justify-items: center;
  transition: opacity 0.15s;

  .emptyDay {
    height: 40px;
  }
  .dayName {
    padding: $spacing 0;
  }

  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    opacity: 0;
  }
}

.spinner {
  position: relative;
  min-height: 100px;
}
</style>
